/** Wire contracts for AIM's Case API (Case Restructure EDD section 2.2). */

export type CaseStatusCode = string;

export interface CaseResource {
  caseId: number;
  clientId: number;
  /** Flat IDs are returned by some Case operations; list responses nest reference data. */
  domainId?: number;
  objectiveTypeId?: number;
  domain?: DomainResource;
  objectiveType?: ObjectiveTypeResource;
  /** Null when AIM cannot map the stored status id to a known key. */
  statusCode: string | null;
  closedReasonCode: string | null;
  checkInCadenceDays: number | null;
  suggestedDisplayFlag: string;
  caseSourceCode: string | null;
  sourceTypeCode: string;
  sourceId: number | null;
  openedAt: string;
  closedAt: string | null;
  /** Create-time fields; GET/list availability is unconfirmed. */
  description?: string | null;
  reason?: string | null;
}

export interface CreateCaseRequest {
  clientId: number;
  domainId: number;
  objectiveTypeId: number;
  statusCode?: string;
  closedReasonCode?: string;
  checkInCadenceDays?: number;
  caseSourceCode?: string;
  sourceTypeCode?: string;
  sourceId?: number;
  description: string;
  reason?: string;
}

export interface UpdateCaseRequest {
  checkInCadenceDays?: number;
  description?: string;
}

export interface QueryCasesRequest {
  clientId?: number;
  statusCode?: CaseStatusCode;
  /** HTTP query parameter name; AIM maps this numeric id internally. */
  assignedTo?: number;
  limit?: number;
  offset?: number;
}

export interface CaseListPayload {
  cases: CaseResource[];
  resultSet: ReferenceListResultSet;
}

export interface TransitionCaseStatusRequest {
  toStatusCode: string;
  closedReasonCode?: string;
  reason?: string;
  expectedCurrentStatusCode?: string;
}

export interface DomainResource {
  domainId: number;
  code: string;
  name: string;
  shortName: string;
  description: string | null;
  displayOrder: number;
}

export interface ObjectiveTypeResource {
  objectiveTypeId: number;
  domainId: number;
  code: string;
  name: string;
  shortName: string;
  description: string | null;
  displayOrder: number;
  plainLanguageName?: string | null;
  clientFacingName?: string | null;
}

export interface ReferenceListResultSet {
  count: number;
  offset: number;
  limit: number;
  allFetched: boolean;
}

export interface DomainListPayload {
  domains: DomainResource[];
  resultSet: ReferenceListResultSet;
}

export interface ObjectiveTypeListPayload {
  objectiveTypes: ObjectiveTypeResource[];
  resultSet: ReferenceListResultSet;
}

export interface CaseStatusTransitionResource {
  caseId: number;
  statusCode: string | null;
  closedReasonCode: string | null;
  closedAt: string | null;
}

export interface CaseApiResult {
  status: number;
  code: string;
  message: string;
  suggestion?: string;
  docs?: string;
}

export interface CaseApiEnvelope<T> {
  result: CaseApiResult;
  payload: T;
}

/** Observed development system-code response (2026-10-06). */
export interface SystemCodeValue {
  systemCodeValueId: number;
  code: string;
  value: string;
}

export interface CaseStatusesPayload {
  CASE_STATUS: SystemCodeValue[];
}

export interface CaseClosedReasonsPayload {
  CASE_CLOSED_REASON: SystemCodeValue[];
}
